import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { collection, getDocs } from 'firebase/firestore';
import { firestore } from '../../database/firebase';
import { validateUserDoc } from '../../schemas/validation';
import {
  HiExclamationTriangle,
  HiArrowPath,
  HiCheckCircle,
  HiXCircle,
  HiDocumentMagnifyingGlass,
  HiUserGroup,
  HiEnvelope,
  HiTrash,
  HiChevronDown,
  HiChevronUp,
} from 'react-icons/hi2';

const CHECKS = [
  { id: 'users', label: 'Users Validation', icon: HiUserGroup, collection: 'users' },
  { id: 'clients', label: 'Clients Validation', icon: HiUserGroup, collection: 'clients' },
  { id: 'duplicates', label: 'Duplicate Emails', icon: HiEnvelope },
  { id: 'orphans', label: 'Orphaned References', icon: HiTrash },
];

const DevDataQualityPage = () => {
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState({});
  const [expandedCheck, setExpandedCheck] = useState(null);
  const [lastRun, setLastRun] = useState(null);

  const validateClientDoc = data => {
    const errors = [];
    if (!data.email || typeof data.email !== 'string' || !data.email.includes('@')) {
      errors.push('Valid email is required');
    }
    const hasName =
      (data.name && typeof data.name === 'string' && data.name.trim()) ||
      (data.firstname && typeof data.firstname === 'string') ||
      (data.displayName && typeof data.displayName === 'string');
    if (!hasName) {
      errors.push('Name or identifier is required');
    }
    return { valid: errors.length === 0, errors };
  };

  const runUserValidation = async colName => {
    const snap = await getDocs(collection(firestore, colName));
    const issues = [];
    const validator = colName === 'clients' ? validateClientDoc : validateUserDoc;
    snap.docs.forEach(d => {
      const data = d.data();
      const validation = validator(data);
      if (!validation.valid) {
        issues.push({
          id: d.id,
          email: data.email || data.userEmail || '—',
          errors: validation.errors,
        });
      }
    });
    return { total: snap.size, issues, passed: snap.size - issues.length };
  };

  const runDuplicateCheck = async () => {
    const [usersSnap, clientsSnap] = await Promise.all([
      getDocs(collection(firestore, 'users')),
      getDocs(collection(firestore, 'clients')),
    ]);
    const emailCounts = {};
    const addEmail = (email, source, id) => {
      if (!email || typeof email !== 'string') return;
      const key = email.toLowerCase().trim();
      if (!emailCounts[key]) emailCounts[key] = [];
      emailCounts[key].push({ source, id });
    };
    usersSnap.docs.forEach(d => addEmail(d.data().email, 'users', d.id));
    clientsSnap.docs.forEach(d => addEmail(d.data().email, 'clients', d.id));
    const duplicates = Object.entries(emailCounts).filter(([, arr]) => arr.length > 1);
    return {
      total: usersSnap.size + clientsSnap.size,
      issues: duplicates.map(([email, refs]) => ({ email, refs })),
      passed:
        usersSnap.size +
        clientsSnap.size -
        duplicates.reduce((s, [, r]) => s + r.length, 0) +
        duplicates.length,
    };
  };

  const runOrphanCheck = async () => {
    const [clientsSnap, orgsSnap] = await Promise.all([
      getDocs(collection(firestore, 'clients')),
      getDocs(collection(firestore, 'organizations')),
    ]);
    const orgIds = new Set(orgsSnap.docs.map(d => d.id));
    const orphans = [];
    clientsSnap.docs.forEach(d => {
      const data = d.data();
      const orgId = data.organizationId || data.organization;
      if (orgId && !orgIds.has(orgId)) {
        orphans.push({ id: d.id, email: data.email || '—', orgId });
      }
    });
    return { total: clientsSnap.size, issues: orphans, passed: clientsSnap.size - orphans.length };
  };

  const runAllChecks = async () => {
    setLoading(true);
    setResults({});

    try {
      const [usersResult, clientsResult, dupResult, orphanResult] = await Promise.all([
        runUserValidation('users').catch(err => ({
          total: 0,
          issues: [],
          passed: 0,
          error: err?.message,
        })),
        runUserValidation('clients').catch(err => ({
          total: 0,
          issues: [],
          passed: 0,
          error: err?.message,
        })),
        runDuplicateCheck().catch(err => ({
          total: 0,
          issues: [],
          passed: 0,
          error: err?.message,
        })),
        runOrphanCheck().catch(err => ({ total: 0, issues: [], passed: 0, error: err?.message })),
      ]);

      setResults({
        users: usersResult,
        clients: clientsResult,
        duplicates: dupResult,
        orphans: orphanResult,
      });
      setLastRun(new Date());
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Data quality check failed:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    runAllChecks();
  }, []);

  const totalIssues = Object.values(results).reduce((s, r) => s + (r?.issues?.length || 0), 0);
  const allPassed = Object.keys(results).length > 0 && totalIssues === 0;

  return (
    <div className="flex flex-col gap-6">
      <motion.header
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between"
      >
        <div className="flex items-center gap-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-violet-600 text-white shadow-lg shadow-violet-500/25">
            <HiExclamationTriangle className="h-6 w-6" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">Data Quality</h1>
            <p className="text-sm text-slate-400">
              Validation checks, duplicate detection, and orphaned references
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {lastRun && (
            <span className="text-xs text-slate-500">Last run: {lastRun.toLocaleTimeString()}</span>
          )}
          <div className="flex gap-2">
            <div
              className={`rounded-lg border px-3 py-1.5 ${
                allPassed
                  ? 'border-emerald-900/50 bg-emerald-950/30'
                  : 'border-amber-900/50 bg-amber-950/30'
              }`}
            >
              <span
                className={`text-xs font-semibold ${
                  allPassed ? 'text-emerald-400' : 'text-amber-400'
                }`}
              >
                {totalIssues} issue{totalIssues !== 1 ? 's' : ''}
              </span>
            </div>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={runAllChecks}
              disabled={loading}
              className="flex items-center gap-2 rounded-lg border border-slate-600 bg-slate-800 px-4 py-2 text-sm font-medium text-slate-200 transition-all hover:border-violet-500/50 hover:bg-slate-700 disabled:opacity-60"
            >
              <HiArrowPath className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Run Checks
            </motion.button>
          </div>
        </div>
      </motion.header>

      <div className="space-y-3">
        {CHECKS.map(check => {
          const result = results[check.id];
          const issueCount = result?.issues?.length || 0;
          const passed = result && issueCount === 0 && !result.error;
          const Icon = check.icon;

          return (
            <motion.div
              key={check.id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className="overflow-hidden rounded-xl border border-slate-700/50 bg-slate-800/50"
            >
              <button
                onClick={() => setExpandedCheck(expandedCheck === check.id ? null : check.id)}
                className="flex w-full items-center justify-between px-4 py-4 text-left transition-colors hover:bg-slate-700/30"
              >
                <div className="flex items-center gap-3">
                  {result ? (
                    passed ? (
                      <HiCheckCircle className="h-5 w-5 shrink-0 text-emerald-500" />
                    ) : (
                      <HiXCircle className="h-5 w-5 shrink-0 text-amber-500" />
                    )
                  ) : (
                    <HiDocumentMagnifyingGlass className="h-5 w-5 shrink-0 text-slate-500" />
                  )}
                  <div>
                    <p className="font-semibold text-white">{check.label}</p>
                    <p className="text-xs text-slate-500">
                      {loading && !result
                        ? 'Running...'
                        : result?.error
                          ? result.error
                          : result
                            ? `${result.passed ?? 0} passed, ${issueCount} issue${issueCount !== 1 ? 's' : ''}`
                            : '—'}
                    </p>
                  </div>
                </div>
                <span className="text-slate-500">
                  {expandedCheck === check.id ? (
                    <HiChevronUp className="h-5 w-5" />
                  ) : (
                    <HiChevronDown className="h-5 w-5" />
                  )}
                </span>
              </button>

              <AnimatePresence>
                {expandedCheck === check.id && result?.issues?.length > 0 && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="border-t border-slate-700/50"
                  >
                    <div className="max-h-64 overflow-y-auto p-4">
                      <ul className="space-y-2">
                        {result.issues.slice(0, 50).map((issue, i) => (
                          <li
                            key={i}
                            className="rounded-lg border border-slate-600/50 bg-slate-900/50 px-3 py-2 text-sm"
                          >
                            {check.id === 'duplicates' ? (
                              <div>
                                <span className="font-medium text-slate-200">{issue.email}</span>
                                <span className="ml-2 text-slate-500">
                                  ({issue.refs?.length} records)
                                </span>
                                <div className="mt-1 text-xs text-slate-500">
                                  {issue.refs?.map(r => `${r.source}/${r.id}`).join(', ')}
                                </div>
                              </div>
                            ) : check.id === 'orphans' ? (
                              <div>
                                <span className="font-medium text-slate-200">
                                  {issue.email || issue.id}
                                </span>
                                <span className="ml-2 text-slate-500">
                                  → org {issue.orgId} (missing)
                                </span>
                              </div>
                            ) : (
                              <div>
                                <span className="font-medium text-slate-200">{issue.email}</span>
                                <span className="ml-2 text-slate-500">({issue.id})</span>
                                <ul className="mt-1 list-inside list-disc text-xs text-amber-300">
                                  {issue.errors?.map((e, j) => (
                                    <li key={j}>{e}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </li>
                        ))}
                      </ul>
                      {result.issues.length > 50 && (
                        <p className="mt-2 text-xs text-slate-500">
                          Showing 50 of {result.issues.length} issues
                        </p>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          );
        })}
      </div>

      {Object.keys(results).length > 0 && totalIssues === 0 && !loading && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="flex flex-col items-center justify-center rounded-xl border border-emerald-900/50 bg-emerald-950/20 py-12 text-center"
        >
          <HiCheckCircle className="h-12 w-12 text-emerald-500" />
          <p className="mt-3 font-semibold text-emerald-300">All checks passed</p>
          <p className="mt-1 text-sm text-slate-400">No data quality issues detected</p>
        </motion.div>
      )}
    </div>
  );
};

export default DevDataQualityPage;
